import {
  Box,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import { Fragment } from 'react'
import { TemplateSummaryOutputs } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import { CycleSpeedView } from 'Features/Projects/Scenarios/Templates/TemplateResults/Results/Results.CycleSpeed.view'

type SummaryItem = {
  label: string
  value: string | number
}

type Summary = SummaryItem[]

type SummaryListProps = {
  summaryListData: Summary
}

type SummaryListItemProps = {
  listItem: SummaryItem
  listIndex: number
}

export const colors = {
  lineColor: '#E7E7E7',
  fontColor: '#000',
  hoverBorderColor: '#000',
  hoverBackgroundColor: '#FFF',
  referenceLineColor: '#006286',
}

const SummaryListItem = ({ listItem }: SummaryListItemProps) => {
  return (
    <ListItem sx={{ padding: '8px 0px', alignItems: 'flex-start' }}>
      <ListItemText primary={listItem.label} />
      <ListItemText primary={listItem.value} sx={{ textAlign: 'right' }} />
    </ListItem>
  )
}

const Column = ({ summaryListData }: SummaryListProps) => {
  return (
    <List
      sx={{
        width: '100%',
        height: '100%',
        padding: '0px',
      }}
    >
      {Boolean(summaryListData?.length) &&
        summaryListData.map((listItem, listIndex) => {
          return (
            <Fragment key={listIndex}>
              <SummaryListItem
                key={listIndex}
                listItem={listItem}
                listIndex={listIndex}
              ></SummaryListItem>
              {listIndex < summaryListData.length - 1 && <Divider />}
            </Fragment>
          )
        })}
    </List>
  )
}

const SummaryList = ({ summaryListData }: SummaryListProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Column summaryListData={summaryListData?.slice(0, 5)} />
      <Divider
        orientation="vertical"
        sx={{
          height: '90%',
          margin: '0px 10px',
        }}
      />
      <Column summaryListData={summaryListData?.slice(5)} />
    </Box>
  )
}

const convertTemplateSummaryToSummaryList = (
  data: TemplateSummaryOutputs[]
): Summary => {
  const summary = data[0]

  const formatValue = (
    value: number | string,
    unit?: string
  ): string | number => {
    if (typeof value === 'number') {
      // Backend persists percentages as fractions (0–1); scale for display.
      const isPct = unit === '%'
      const scaled = isPct ? value * 100 : value
      const formatted = scaled.toFixed(isPct ? 1 : 2)
      return unit ? `${formatted} ${unit}` : formatted
    }
    return value
  }

  return [
    {
      label: 'Avg Changeovers/Week',
      value: formatValue(summary.avgChangeOversPerWeek),
    },
    {
      label: 'Avg Utilisation Hours (Non-FOQ)/Week',
      value: formatValue(summary.avgUtilisationHoursNonFOQPerWeek),
    },
    {
      label: 'Avg Changeover per Product',
      value: formatValue(summary.avgChangeOverPerProduct),
    },
    {
      label: 'Weighted Avg Run Rate',
      value: formatValue(summary.weightedAvgRunRate),
    },
    {
      label: 'Avg Cycle Speed',
      value: formatValue(summary.avgCycleSpeed),
    },
    {
      label: '% Changeover',
      value: formatValue(summary.percentageChangeOver, '%'),
    },
    {
      label: 'Utilisation Volatility',
      value: formatValue(summary.utilisationVolatility, '%'),
    },
    {
      label: 'Max Hours',
      value: formatValue(summary.maxHours),
    },
    {
      label: 'Workcentre Capacity Initial',
      value: formatValue(summary.workcentreCapacityInitial),
    },
    {
      label: 'Adjusted Capacity',
      value: formatValue(summary.adjustedCapacity),
    },
  ]
}

export const PlanTemplateSummaryView = ({
  templateSummaryData,
}: {
  templateSummaryData: TemplateSummaryOutputs[]
}) => {
  const summaryListData =
    convertTemplateSummaryToSummaryList(templateSummaryData)

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      sx={{
        width: '100%',
        height: '100%',
        padding: '16px 24px',
      }}
    >
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 600 }}>
          Plan template summary
        </Typography>
        <Divider sx={{ width: '100%' }} />
      </Box>
      <SummaryList summaryListData={summaryListData} />
      <CycleSpeedView templateSummaryData={templateSummaryData} />
    </Box>
  )
}
